import { createSlice, createAsyncThunk, createAction } from '@reduxjs/toolkit'
import { RequestPayload, updateFieldMappingDetails, updateValueMapping } from '../../api/cafmApi'
import * as _ from 'lodash'
import { getFieldType } from '../../utils/splitName'
import { NewAddedValueMapping } from '../../components/EditFieldMapping/ValueMapping'

// Async thunk to fetch users
export const EditFieldMappingPayload = createAsyncThunk<
  Record<string, string>,
  RequestPayload,
  { rejectValue: string }
>('updatePayload/EditFieldMappingPayload', async (payload, { rejectWithValue, getState }) => {
  try {
    const state: any = getState()
    const OpeationDetailsState = state.OpeationDetails.OpeationDetails
    if (OpeationDetailsState.result.exist) {
      throw new Error('Already Exist with this criteria')
    }
    const response = await updateFieldMappingDetails(payload!)
    return response.data
  } catch (error) {
    return rejectWithValue(error.message)
  }
})

export const resetEditFieldMapping = createAction('fieldMappingEdit/reset')

// Async thunk to fetch users
export const EditValueMappingPayload = createAsyncThunk<
  Record<string, string>,
  NewAddedValueMapping,
  { rejectValue: string }
>('updatePayload/EditValueMappingPayload', async (payload, { rejectWithValue }) => {
  try {
    const response = await updateValueMapping(payload!)
    return response.data
  } catch (error) {
    return rejectWithValue(error.message)
  }
})

const fieldTypeMapping: Record<string, string | null> = {
  'request.body': 'body',
  'request.pathparam': 'params',
  'request.queryparam': 'query',
}

const initialState: {
  updatePayload: RequestPayload
  loading: boolean
  error: string | null
  successful: boolean
  loadingValueMapping: boolean
  successValueMapping: boolean
} = {
  updatePayload: {
    operation_id: '',
    api_endpoint_url: '',
    api_method: '',
    api_header_content_type: '',
    decider_fields_name: '',
    decider_fields_value: [],
    added_new_field_mapping: [],
    delete_field_mapping: [],
    update_field_mapping: [],
    needs_delay: false,
    delay_mins: undefined,
    needs_escalation: false,
    escalation_email: null,
    enable_email: false,
    email_address: null,
    configure_status: false,
    file_enabled_statuses: [],
  },
  loading: false,
  error: null,
  successful: false,
  loadingValueMapping: false,
  successValueMapping: false,
}

const updateFieldMappingSlice = createSlice({
  name: 'updatePayload',
  initialState,
  reducers: {
    setPayload: (state, action) => {
      const { type, payload } = action.payload
      if (type === 'SET_OPERATION_AND_DECIDER_VAL') {
        state.updatePayload.operation_id = payload.operation_id
        state.updatePayload.decider_fields_value = payload.decider_fields_value
      } else if (type === 'UPDATE_DECIDER_FIELDS') {
        // Handle multi-decider structure: extract parent field_value + all multiDecider field_values
        // and join them into a comma-separated string wrapped in an array
        // Format: ["12,isPresent,Unplanned,true,true"] NOT ["12"]

        if (payload.EditFieldMapping?.decider_fields_details?.[0]) {
          const firstDecider = payload.EditFieldMapping.decider_fields_details[0]
          const parentValue = firstDecider.field_value || ''
          const expectedDeciderNames =
            typeof state.updatePayload.decider_fields_name === 'string' && state.updatePayload.decider_fields_name.trim()
              ? state.updatePayload.decider_fields_name.split(',').map((name) => name.trim())
              : []
          const existingCombinedValue = state.updatePayload.decider_fields_value?.[0]
          const existingDeciderValues =
            typeof existingCombinedValue === 'string' && existingCombinedValue.trim()
              ? existingCombinedValue.split(',').map((value) => value.trim())
              : []

          // Check if multiDeciders exist
          if (
            firstDecider.multiDeciders &&
            Array.isArray(firstDecider.multiDeciders) &&
            firstDecider.multiDeciders.length > 0
          ) {
            // Extract all multiDecider values
            const multiDeciderValues = firstDecider.multiDeciders.map((md: any) => md.field_value)

            let mergedValues = [parentValue, ...multiDeciderValues]

            // Preserve missing trailing decider values from current payload state
            // (e.g. soType in edit flow where API multiDeciders may not include it yet).
            if (expectedDeciderNames.length > mergedValues.length && existingDeciderValues.length) {
              mergedValues = expectedDeciderNames.map((_, index) => mergedValues[index] ?? existingDeciderValues[index] ?? '')
            }

            // Join parent value + multiDecider values into comma-separated string
            const allValues = mergedValues.join(',')
            state.updatePayload.decider_fields_value = [allValues]

            console.log('✅ UPDATE_DECIDER_FIELDS - Set multi-decider values:', {
              parentValue,
              multiDeciderValues,
              mergedValues,
              finalValue: [allValues],
            })
          } else {
            // No multiDeciders from API. Preserve missing values from current payload if decider name expects more.
            const mergedValues =
              expectedDeciderNames.length > 1 && existingDeciderValues.length
                ? expectedDeciderNames.map((_, index) => (index === 0 ? parentValue : existingDeciderValues[index] ?? ''))
                : [parentValue]

            state.updatePayload.decider_fields_value = [mergedValues.join(',')]

            console.log('✅ UPDATE_DECIDER_FIELDS - Set single-decider value:', [mergedValues.join(',')])
          }
        } else {
          // Fallback: Handle legacy structure if no EditFieldMapping data
          const key = Object.keys(payload.decider_fields_value)[0]
          let isPresent = false
          if (key && payload['decider_fields_value'][key] && Array.isArray(payload['decider_fields_value'][key])) {
            const replaceKey = payload['decider_fields_value'][key].map((p: string) => {
              if (p.includes('isPresent')) {
                isPresent = true
              }
              return p.replace(`${key}_`, '')
            })

            if (isPresent) {
              state.updatePayload.decider_fields_value = [
                payload.EditFieldMapping.decider_fields_details[0].field_value,
              ]
            } else {
              state.updatePayload.decider_fields_value = replaceKey
            }
          } else {
            // Handle case where no decider fields are selected
            state.updatePayload.decider_fields_value = []
          }
        }
      } else if (type === 'UPDATE_DECIDER_NAME') {
        state.updatePayload.decider_fields_name = payload.decider_fields_name
      } else if (type === 'ADD_FIELD_MAPPING') {
        state.updatePayload.added_new_field_mapping?.push({
          new_field_id: payload.new_field_id,
          field_name: payload.destination_object_field_path,
          data_type: payload.source_object_field_type,
          is_mandatory: payload.required_for_request,
          default_value: payload.destination_default_value || null,
          request_location: payload.field_type,
          source_object_field_name: payload.source_object_field_name,
        })
      } else if (type === 'MATCH_FIELD') {
        const { selectedValues, record } = payload

        if (record.field_mapping_id) {
          if (state.updatePayload.update_field_mapping?.length) {
            // Find the matching record
            const foundRecord = state.updatePayload.update_field_mapping.find(
              (f) => f.field_id === record.field_mapping_id,
            )

            state.updatePayload.update_field_mapping = [
              ...state.updatePayload.update_field_mapping.filter((f) => f.field_id !== record.field_mapping_id),
              {
                ...(foundRecord as any),
                field_id: record.field_mapping_id, // CRITICAL: Always preserve field_id
                source_object_field_name: selectedValues,
                request_location: getFieldType(record.field_type),
              },
            ]
          } else {
            state.updatePayload.update_field_mapping = [
              {
                field_id: record.field_mapping_id, // CRITICAL: Always include field_id
                source_object_field_name: selectedValues,
                field_name: record.destination_object_field_path,
                data_type: record.source_object_field_type,
                is_mandatory: record.required_for_request,
                default_value: record.destination_default_value || null,
                request_location: getFieldType(record.field_type),
              },
            ]
          }
        } else {
          // Find the matching record
          const foundRecord = state.updatePayload.added_new_field_mapping?.find(
            (f) => f.new_field_id === record.new_field_id,
          )

          state.updatePayload.added_new_field_mapping = [
            ...(state.updatePayload.added_new_field_mapping || []).filter(
              (f) => f.new_field_id !== record.new_field_id,
            ),
            {
              ...(foundRecord as any),
              source_object_field_name: selectedValues,
            },
          ]
        }
      } else if (type === 'SET_DELETE_FIELDS') {
        state.updatePayload.added_new_field_mapping = state.updatePayload.added_new_field_mapping?.filter(
          (a) => a.new_field_id !== payload.new_field_id,
        )
        state.updatePayload.update_field_mapping = state.updatePayload.update_field_mapping?.filter(
          (u) => u.field_id !== payload.field_id,
        )

        state.updatePayload.delete_field_mapping = _.uniq(
          state.updatePayload.delete_field_mapping?.concat(payload.field_mapping_id),
        )
      } else if (type === 'UPDATE_FIELDS_MAPPING') {
        payload.field_type = getFieldType(payload.field_type)
        if (state.updatePayload.update_field_mapping?.length) {
          // Find the matching record
          const foundRecord = state.updatePayload.update_field_mapping.find((f) => f.field_id === payload.field_id)

          state.updatePayload.update_field_mapping = [
            ...state.updatePayload.update_field_mapping.filter((f) => f.field_id !== payload.field_id),
            {
              ...(foundRecord as any),
              field_id: payload.field_id, // CRITICAL: Always include field_id
              field_name: payload.destination_object_field_path,
              data_type: payload.source_object_field_type,
              is_mandatory: payload.required_for_request,
              default_value: payload.destination_default_value || null,
              request_location: payload.field_type,
              source_object_field_name: payload.source_object_field_name,
            },
          ]
        } else {
          state.updatePayload.update_field_mapping = [
            {
              field_id: payload.field_id, // CRITICAL: Always include field_id
              field_name: payload.destination_object_field_path,
              data_type: payload.source_object_field_type,
              is_mandatory: payload.required_for_request,
              default_value: payload.destination_default_value || null,
              request_location: payload.field_type,
              source_object_field_name: payload.source_object_field_name,
            },
          ]
        }
      } else if (type === 'UPDATE_NEW_FIELDS_MAPPING') {
        if (state.updatePayload.added_new_field_mapping?.length) {
          // Find the matching record
          const foundRecord = state.updatePayload.added_new_field_mapping.find(
            (f) => f.new_field_id === payload.new_field_id,
          )

          state.updatePayload.added_new_field_mapping = [
            ...state.updatePayload.added_new_field_mapping.filter((f) => f.new_field_id !== payload.new_field_id),
            {
              ...(foundRecord as any),
              field_name: payload.destination_object_field_path,
              data_type: payload.source_object_field_type,
              is_mandatory: payload.required_for_request,
              default_value: payload.destination_default_value || null,
              request_location: payload.field_type,
              source_object_field_name: payload.source_object_field_name,
            },
          ]
        }
      } else if (type === 'SELECT_DECIDER_FIELDS') {
        const excludeIds: string[] = []

        state.updatePayload.added_new_field_mapping = state.updatePayload.added_new_field_mapping?.map((a) => {
          return {
            ...a,
            request_location: a.request_location
              ? a.request_location.toLowerCase() === 'request.body' || a.request_location === 'body'
                ? 'body'
                : a.request_location.toLowerCase() === 'request.pathparam' || a.request_location === 'params'
                  ? 'params'
                  : 'query'
              : '',
            source_object_field_name: 'Status',
          }
        })

        state.updatePayload.update_field_mapping = state.updatePayload.update_field_mapping?.map((a) => {
          excludeIds.push(a.field_id)
          return {
            ...a,
            request_location: a.request_location
              ? a.request_location.toLowerCase() === 'request.body' || a.request_location === 'body'
                ? 'body'
                : a.request_location.toLowerCase() === 'request.pathparam' || a.request_location === 'params'
                  ? 'params'
                  : 'query'
              : '',
            source_object_field_name: 'Status',
          }
        })

        const filteredArray = _.reject(
          payload.EditFieldMapping.field_mapping_details,
          (item) => excludeIds.includes(item.field_mapping_id) || item.new_field_id,
        )

        filteredArray.map((f) => {
          state.updatePayload.update_field_mapping?.push({
            field_id: f.field_mapping_id,
            source_object_field_name: 'Status',
            field_name: f.destination_object_field_path,
            data_type:
              (fieldTypeMapping[f.source_object_field_type.toLowerCase()] as null | 'body' | 'query' | 'params') ||
              f.source_object_field_type,
            is_mandatory: f.required_for_request,
            default_value: f.destination_default_value || null,
            request_location: f.field_type
              ? f.field_type?.toLowerCase() === 'request.body' || f.field_type === 'body'
                ? 'body'
                : f.field_type.toLowerCase() === 'request.pathparam' || f.field_type === 'params'
                  ? 'params'
                  : 'query'
              : '',
          })
        })
      } else if (type === 'UPDATE_ENDPOINT') {
        state.updatePayload.api_endpoint_url = payload.api_endpoint_url
      } else if (type === 'UPDATE_ENDPOINT_METHOD') {
        state.updatePayload.api_method = payload.api_method
      } else if (type === 'SET_ERROR') {
        state.error = payload.error
      } else if (type === 'SET_OPERATION') {
        state.updatePayload.operation_id = payload.operation_id
      } else if (type === 'UPDATE_API_HEADER') {
        state.updatePayload.api_header_content_type = payload.api_headers
      } else if (type === 'UPDATE_DELAY_FIELDS') {
        state.updatePayload.needs_delay = payload.needs_delay
        state.updatePayload.delay_mins = payload.delay_mins
      } else if (type === 'UPDATE_EMAIL_ESCALATION_FIELDS') {
        state.updatePayload.needs_escalation = payload.needs_escalation
        state.updatePayload.escalation_email =
          payload.escalation_email !== undefined ? payload.escalation_email : state.updatePayload.escalation_email
        state.updatePayload.enable_email = payload.enable_email
        state.updatePayload.email_address = payload.email_address
      } else if (type === 'UPDATE_FILE_STATUS_CONFIG') {
        state.updatePayload.configure_status = payload.configure_status
        state.updatePayload.file_enabled_statuses = payload.file_enabled_statuses
      }
    },
    setValueMapping: (state, action) => {
      const { new_field_id, value_mapping_type, values_mapping } = action.payload

      const foundRecord = state.updatePayload.added_new_field_mapping!.find((f) => f.new_field_id === new_field_id)

      if (!_.isEmpty(foundRecord)) {
        state.updatePayload.added_new_field_mapping = state.updatePayload
          .added_new_field_mapping!.filter((f) => f.new_field_id !== new_field_id)
          .concat({
            ...foundRecord,
            match_values: {
              match_value_type: value_mapping_type,
              values_mapping,
            },
          })
      }
    },
    resetValueMappingSuccess: (state) => {
      state.successValueMapping = false
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(EditFieldMappingPayload.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(EditFieldMappingPayload.fulfilled, (state) => {
        state.loading = false
        state.successful = true
      })
      .addCase(EditFieldMappingPayload.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(resetEditFieldMapping, () => {
        return initialState
      })
      .addCase(EditValueMappingPayload.pending, (state) => {
        state.loadingValueMapping = true
      })
      .addCase(EditValueMappingPayload.fulfilled, (state) => {
        state.loadingValueMapping = false
        state.successValueMapping = true
      })
      .addCase(EditValueMappingPayload.rejected, (state) => {
        state.loadingValueMapping = false
        state.successValueMapping = false
      })
  },
})

export const { setPayload, setValueMapping, resetValueMappingSuccess } = updateFieldMappingSlice.actions
export default updateFieldMappingSlice.reducer
